/**
 * Consolidated Report Generator
 * Generates all branded reports for a project
 */

import { generateExecutiveSummaryPDF, ExecutiveSummaryData } from './executiveSummaryPDF';
import { generateProcessFlowComparisonPDF, ProcessFlowComparisonData } from './processFlowComparisonPDF';
import { generateImplementationRoadmapPDF, ImplementationRoadmapData } from './implementationRoadmapPDF';
import { generateImplementationGuidePDF, ImplementationGuideData } from './implementationGuidePDF';
import { generateConsolidatedReportPDF, ConsolidatedReportData } from './consolidatedReportPDF';
import clientBrandingAPI from '../clientBrandingAPI';
import { OptimizationOpportunity } from '../processOptimizationAPI';
import toast from 'react-hot-toast';

export interface ReportGenerationOptions {
  projectId: number;
  projectName: string;
  opportunities: OptimizationOpportunity[];
  totalSteps: number;
  coveragePercentage: number;
}

/**
 * Generate all reports with client branding
 */
export async function generateAllReports(options: ReportGenerationOptions): Promise<void> {
  try {
    toast.loading('Fetching branding settings...', { id: 'generate-reports' });

    // Fetch branding (with fallback to default)
    const branding = await clientBrandingAPI.getBrandingOrDefault(options.projectId);

    toast.loading('Generating Executive Summary...', { id: 'generate-reports' });

    // Calculate total time reduction
    const totalTimeReduction = calculateTotalTimeReduction(options.opportunities);

    // Generate Executive Summary PDF
    const summaryData: ExecutiveSummaryData = {
      projectName: options.projectName,
      totalSteps: options.totalSteps,
      opportunities: options.opportunities,
      totalTimeReduction,
      coveragePercentage: options.coveragePercentage
    };

    generateExecutiveSummaryPDF(summaryData, {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    });

    toast.dismiss('generate-reports');
    toast.success('Executive Summary PDF generated successfully!');

  } catch (error: any) {
    console.error('Error generating reports:', error);
    toast.dismiss('generate-reports');
    toast.error(error.message || 'Failed to generate reports');
  }
}

/**
 * Generate Process Flow Comparison PDF for a specific opportunity
 */
export async function generateProcessFlowReport(
  projectId: number,
  _opportunityId: number,
  _opportunityTitle: string,
  flowData: ProcessFlowComparisonData
): Promise<void> {
  try {
    toast.loading('Generating Process Flow Comparison PDF...', { id: 'process-flow-pdf' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);

    generateProcessFlowComparisonPDF(flowData, {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    });

    toast.dismiss('process-flow-pdf');
    toast.success('Process Flow Comparison PDF generated successfully!');
  } catch (error: any) {
    console.error('Error generating process flow PDF:', error);
    toast.dismiss('process-flow-pdf');
    toast.error(error.message || 'Failed to generate process flow PDF');
  }
}

/**
 * Generate Implementation Roadmap PDF for a specific opportunity
 */
export async function generateRoadmapReport(
  projectId: number,
  _opportunityId: number,
  roadmapData: ImplementationRoadmapData
): Promise<void> {
  try {
    toast.loading('Generating Implementation Roadmap PDF...', { id: 'roadmap-pdf' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);

    generateImplementationRoadmapPDF(roadmapData, {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    });

    toast.dismiss('roadmap-pdf');
    toast.success('Implementation Roadmap PDF generated successfully!');
  } catch (error: any) {
    console.error('Error generating roadmap PDF:', error);
    toast.dismiss('roadmap-pdf');
    toast.error(error.message || 'Failed to generate roadmap PDF');
  }
}

/**
 * Generate Implementation Guide PDF for a specific opportunity
 */
export async function generateImplementationGuideReport(
  projectId: number,
  _opportunityId: number,
  guideData: ImplementationGuideData
): Promise<void> {
  try {
    toast.loading('Generating Implementation Guide PDF...', { id: 'guide-pdf' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);

    generateImplementationGuidePDF(guideData, {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    });

    toast.dismiss('guide-pdf');
    toast.success('Implementation Guide PDF generated successfully!');
  } catch (error: any) {
    console.error('Error generating implementation guide PDF:', error);
    toast.dismiss('guide-pdf');
    toast.error(error.message || 'Failed to generate implementation guide PDF');
  }
}

/**
 * Generate comprehensive report package for a specific opportunity (3 separate PDFs)
 * Includes: Process Flow Comparison, Implementation Roadmap, and Implementation Guide
 */
export async function generateComprehensiveReportPackage(
  projectId: number,
  _opportunityId: number,
  _opportunityTitle: string,
  flowData: ProcessFlowComparisonData,
  roadmapData: ImplementationRoadmapData,
  guideData: ImplementationGuideData
): Promise<void> {
  try {
    toast.loading('Generating comprehensive report package...', { id: 'comprehensive-reports' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);

    const brandingConfig = {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    };

    // Generate all three reports
    toast.loading('Generating Process Flow Comparison...', { id: 'comprehensive-reports' });
    generateProcessFlowComparisonPDF(flowData, brandingConfig);

    toast.loading('Generating Implementation Roadmap...', { id: 'comprehensive-reports' });
    generateImplementationRoadmapPDF(roadmapData, brandingConfig);

    toast.loading('Generating Implementation Guide...', { id: 'comprehensive-reports' });
    generateImplementationGuidePDF(guideData, brandingConfig);

    toast.dismiss('comprehensive-reports');
    toast.success('All reports generated successfully! Check your downloads folder.');
  } catch (error: any) {
    console.error('Error generating comprehensive reports:', error);
    toast.dismiss('comprehensive-reports');
    toast.error(error.message || 'Failed to generate comprehensive reports');
  }
}

/**
 * Generate single consolidated PDF containing all sections
 * This is the recommended approach for a cohesive report
 */
export async function generateConsolidatedReport(
  projectId: number,
  projectName: string,
  opportunityTitle: string,
  data: {
    totalSteps?: number;
    coveragePercentage?: number;
    totalTimeReduction?: string;
    processFlow?: ProcessFlowComparisonData;
    roadmap?: ImplementationRoadmapData;
    implementationGuide?: ImplementationGuideData;
  }
): Promise<void> {
  try {
    toast.loading('Generating consolidated comprehensive report...', { id: 'consolidated-report' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);

    const consolidatedData: ConsolidatedReportData = {
      projectName,
      opportunityTitle,
      totalSteps: data.totalSteps,
      coveragePercentage: data.coveragePercentage,
      totalTimeReduction: data.totalTimeReduction,
      processFlow: data.processFlow,
      roadmap: data.roadmap,
      implementationGuide: data.implementationGuide
    };

    generateConsolidatedReportPDF(consolidatedData, {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    });

    toast.dismiss('consolidated-report');
    toast.success('Consolidated report generated successfully!');
  } catch (error: any) {
    console.error('Error generating consolidated report:', error);
    toast.dismiss('consolidated-report');
    toast.error(error.message || 'Failed to generate consolidated report');
  }
}

/**
 * Generate comprehensive report for ALL opportunities in a single PDF
 * Includes all sections for each opportunity
 */
export async function generateAllOpportunitiesReport(
  projectId: number,
  projectName: string,
  opportunities: OptimizationOpportunity[],
  totalSteps: number
): Promise<void> {
  try {
    toast.loading('Generating comprehensive report for all opportunities...', { id: 'all-opps-report' });

    const branding = await clientBrandingAPI.getBrandingOrDefault(projectId);
    const brandingConfig = {
      companyName: branding.company_name || 'Client Company',
      logo: branding.logo,
      colorPalette: branding.color_palette
    };

    // Import BrandedPDFGenerator
    const { BrandedPDFGenerator } = await import('../../utils/brandedPDFGenerator');
    const generator = new BrandedPDFGenerator(brandingConfig);

    // TITLE PAGE
    generator.addBrandedHeader(
      'Comprehensive Automation Report',
      projectName
    );

    generator.addSpace(12);

    // EXECUTIVE SUMMARY
    generator.addSectionHeader('Executive Summary');
    generator.addSpace(3);

    const totalTimeReduction = calculateTotalTimeReduction(opportunities);

    generator.addKeyValue('Project', projectName);
    generator.addKeyValue('Total Process Steps Analyzed', totalSteps.toString());
    generator.addKeyValue('Automation Opportunities Identified', opportunities.length.toString());
    generator.addKeyValue('Estimated Total Time Reduction', totalTimeReduction);

    // Portfolio Business Case — only when grounded data exists
    const groundedOpps = opportunities.filter(o => o.data_source === 'grounded');
    if (groundedOpps.length > 0) {
      const totalAnnualBenefit = groundedOpps.reduce((sum, o) => sum + (Number(o.total_annual_benefit) || 0), 0);
      const totalNPV = groundedOpps.reduce((sum, o) => sum + (Number(o.npv_3year) || 0), 0);
      const avgROI = groundedOpps.reduce((sum, o) => sum + (Number(o.roi_3year_pct) || 0), 0) / groundedOpps.length;
      const withPayback = groundedOpps.filter(o => o.payback_months);
      const avgPayback = withPayback.length > 0
        ? withPayback.reduce((sum, o) => sum + (Number(o.payback_months) || 0), 0) / withPayback.length
        : null;

      const formatMoney = (val: number) =>
        val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : `$${Math.round(val / 1000)}K`;

      generator.addSpace(6);
      generator.addText('Portfolio Business Case (Grounded Analysis)', 11, true);
      generator.addSpace(2);
      generator.addKeyValue('Grounded Opportunities', `${groundedOpps.length} of ${opportunities.length} with validated data`);
      generator.addKeyValue('Total Annual Benefit', formatMoney(totalAnnualBenefit));
      generator.addKeyValue('Portfolio NPV (3-Year)', formatMoney(totalNPV));
      generator.addKeyValue('Average ROI (3-Year)', `${avgROI.toFixed(0)}%`);
      if (avgPayback != null) generator.addKeyValue('Average Payback Period', `${avgPayback.toFixed(1)} months`);
      generator.addKeyValue('Calculation Methodology', 'Grounded Calculation Engine v1.0 — 10% discount rate, 3-year horizon');
    }

    generator.addSpace(12);

    // TABLE OF CONTENTS
    generator.addSectionHeader('Opportunities Overview');
    generator.addSpace(3);
    opportunities.forEach((opp, index) => {
      generator.addBullet(`${index + 1}. ${opp.title} - ${opp.estimated_time_reduction}`);
    });

    generator.addSpace(15);

    // LOOP THROUGH EACH OPPORTUNITY
    opportunities.forEach((opportunity, oppIndex) => {
      generator.checkPageBreak(40);

      // Opportunity Header
      generator.addColoredBox(
        `Opportunity ${oppIndex + 1}: ${opportunity.title}`,
        brandingConfig.colorPalette.primary
      );

      generator.addSpace(3);
      generator.addKeyValue('Type', opportunity.inefficiency_type);
      generator.addKeyValue('Time Reduction', opportunity.estimated_time_reduction);
      generator.addKeyValue('Complexity', opportunity.implementation_complexity);

      generator.addSpace(6);

      // Description
      generator.addSubsectionHeader('Description');
      generator.addSpace(2);
      generator.addText(opportunity.description, 10);

      generator.addSpace(6);

      // Solution Approach
      generator.addSubsectionHeader('Solution Approach');
      generator.addSpace(2);
      generator.addText(opportunity.solution_approach, 10);

      generator.addSpace(8);

      // Process Flow Transformation (if affected steps available)
      if (opportunity.affected_steps && opportunity.affected_steps.length > 0) {
        generator.checkPageBreak(50); // Ensure enough space for the whole section
        generator.addSubsectionHeader('Process Flow Transformation');
        generator.addSpace(2);

        // Parse time reduction percentage
        const timeReductionMatch = opportunity.estimated_time_reduction.match(/(\d+)%/);
        const reductionPercentage = timeReductionMatch ? parseInt(timeReductionMatch[1]) : 30;

        generator.addKeyValue('Steps Impacted', opportunity.affected_steps.length.toString());
        generator.addKeyValue('Estimated Time Reduction', `${reductionPercentage}%`);

        generator.addSpace(4);
        generator.checkPageBreak(20); // Keep before automation section together
        generator.addText('Before Automation:', 10, true);
        generator.addSpace(2);
        opportunity.affected_steps.forEach((step: any, idx: number) => {
          const stepName = step.name || step.processStepName || `Step ${idx + 1}`;
          generator.addBullet(`${stepName}${step.owner ? ` (Owner: ${step.owner})` : ''}`, 1);
        });

        generator.addSpace(4);
        generator.checkPageBreak(20); // Keep after automation section together
        generator.addText('After Automation:', 10, true);
        generator.addSpace(2);
        generator.addBullet(`Automated workflow reduces manual touchpoints by ${reductionPercentage}%`, 1, brandingConfig.colorPalette.success);
        generator.addBullet('Streamlined handoffs and reduced processing time', 1, brandingConfig.colorPalette.success);
        if (opportunity.primary_tools && opportunity.primary_tools.length > 0) {
          generator.addBullet(`Implemented via ${opportunity.primary_tools[0]}`, 1, brandingConfig.colorPalette.success);
        }

        generator.addSpace(4);

        // Transformation Metrics
        generator.checkPageBreak(35); // Keep transformation metrics together (heading + 3 key-values)
        generator.addText('Transformation Metrics:', 10, true);
        generator.addSpace(2);
        const estimatedHoursSaved = Math.round(opportunity.affected_steps.length * 2 * (reductionPercentage / 100));
        generator.addKeyValue('  Effort Reduction', `~${estimatedHoursSaved} hours per cycle`);
        generator.addKeyValue('  Steps Automated', opportunity.affected_steps.length.toString());
        generator.addKeyValue('  Manual Handoffs Reduced', Math.max(1, opportunity.affected_steps.length - 1).toString());

        generator.addSpace(8);
      }

      // Implementation Factors (if available)
      if (opportunity.governance_complexity_score || opportunity.automation_readiness_score) {
        generator.checkPageBreak(35); // Ensure enough space for the whole section
        generator.addSubsectionHeader('Implementation Factors');
        generator.addSpace(2);

        if (opportunity.governance_complexity_score) {
          generator.addKeyValue('Governance Complexity', `${opportunity.governance_complexity_score}/10`);
        }
        if (opportunity.automation_readiness_score) {
          generator.addKeyValue('Automation Readiness', `${opportunity.automation_readiness_score}/10`);
        }
        if (opportunity.integration_complexity_score) {
          generator.addKeyValue('Integration Complexity', `${opportunity.integration_complexity_score}/10`);
        }
        if (opportunity.regulatory_risk_score) {
          generator.addKeyValue('Regulatory Risk', `${opportunity.regulatory_risk_score}/10`);
        }

        generator.addSpace(8);
      }

      // Recommended Tools
      if (opportunity.primary_tools && opportunity.primary_tools.length > 0) {
        generator.checkPageBreak(25); // Ensure enough space for the whole section
        generator.addSubsectionHeader('Recommended Tools');
        generator.addSpace(2);
        opportunity.primary_tools.forEach(tool => {
          generator.addBullet(tool);
        });
        generator.addSpace(8);
      }

      // Phased Rollout Plan (if available)
      if (opportunity.phased_rollout_plan) {
        generator.checkPageBreak(40); // Ensure enough space for the whole section
        generator.addSubsectionHeader('Phased Implementation Plan');
        generator.addSpace(2);

        generator.addKeyValue('Total Phases', opportunity.phased_rollout_plan.total_phases?.toString() || 'N/A');
        generator.addKeyValue('Recommended Starting Phase', `Phase ${opportunity.phased_rollout_plan.recommended_starting_phase || 1}`);

        if (opportunity.phased_rollout_plan.phase_1) {
          const phase1 = opportunity.phased_rollout_plan.phase_1;
          generator.checkPageBreak(18); // Keep phase details together
          generator.addSpace(4);
          generator.addText(`Phase 1: ${phase1.phase_name || 'Initial Phase'}`, 11, true);
          generator.addSpace(2);
          if (phase1.timeline) generator.addKeyValue('  Timeline', phase1.timeline);
          if (phase1.value_delivered) generator.addKeyValue('  Value', phase1.value_delivered);
        }

        if (opportunity.phased_rollout_plan.phase_2) {
          const phase2 = opportunity.phased_rollout_plan.phase_2;
          generator.checkPageBreak(18); // Keep phase details together
          generator.addSpace(4);
          generator.addText(`Phase 2: ${phase2.phase_name || 'Advanced Phase'}`, 11, true);
          generator.addSpace(2);
          if (phase2.timeline) generator.addKeyValue('  Timeline', phase2.timeline);
          if (phase2.value_delivered) generator.addKeyValue('  Value', phase2.value_delivered);
        }

        if (opportunity.phased_rollout_plan.phase_3) {
          const phase3 = opportunity.phased_rollout_plan.phase_3;
          generator.checkPageBreak(18); // Keep phase details together
          generator.addSpace(4);
          generator.addText(`Phase 3: ${phase3.phase_name || 'Full Implementation'}`, 11, true);
          generator.addSpace(2);
          if (phase3.timeline) generator.addKeyValue('  Timeline', phase3.timeline);
          if (phase3.value_delivered) generator.addKeyValue('  Value', phase3.value_delivered);
        }

        generator.addSpace(8);
      }

      // Business Case (only when grounded data available)
      if (opportunity.data_source === 'grounded') {
        generator.checkPageBreak(45);
        generator.addSubsectionHeader('Business Case (Grounded Analysis)');
        generator.addSpace(2);

        const fmtMoney = (val: number) =>
          val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : `$${Math.round(val / 1000)}K`;

        generator.addKeyValue('Data Source', 'Grounded — Calculated from actual client metrics');
        if (opportunity.annual_hours_saved != null) {
          generator.addKeyValue('Annual Hours Saved', `${Number(opportunity.annual_hours_saved).toLocaleString()} hours/year`);
        }
        if (opportunity.weighted_hourly_rate != null) {
          generator.addKeyValue('Weighted Hourly Rate', `$${Number(opportunity.weighted_hourly_rate).toFixed(2)}/hour`);
        }
        if (opportunity.total_annual_benefit != null) {
          generator.addKeyValue('Total Annual Benefit', fmtMoney(Number(opportunity.total_annual_benefit)));
        }
        if (opportunity.npv_3year != null) {
          generator.addKeyValue('Net Present Value (3-Year)', fmtMoney(Number(opportunity.npv_3year)));
        }
        if (opportunity.roi_3year_pct != null) {
          generator.addKeyValue('Return on Investment (3yr)', `${Number(opportunity.roi_3year_pct).toFixed(0)}%`);
        }
        if (opportunity.payback_months != null) {
          generator.addKeyValue('Payback Period', `${Number(opportunity.payback_months).toFixed(1)} months`);
        }

        generator.addSpace(8);
      }

      // Compliance Requirements Summary
      if (opportunity.compliance_requirements) {
        generator.checkPageBreak(25); // Keep compliance section together
        generator.addSubsectionHeader('Compliance Considerations');
        generator.addSpace(2);

        const reqs = opportunity.compliance_requirements as any;

        if (reqs.sox_controls?.applicable) {
          generator.addBullet('SOX Controls Required', 0, brandingConfig.colorPalette.warning);
        }
        if (reqs.data_governance?.applicable) {
          generator.addBullet('Data Governance Controls Required', 0, brandingConfig.colorPalette.warning);
        }
        if (reqs.change_management?.applicable) {
          generator.addBullet('Change Management Process Required', 0, brandingConfig.colorPalette.warning);
        }

        generator.addSpace(8);
      }

      // Start next opportunity on new page for clear separation
      if (oppIndex < opportunities.length - 1) {
        generator.addPageBreak();
      }
    });

    // FOOTER
    generator.addBrandedFooter('Comprehensive Automation Report');

    // SAVE
    const filename = `Comprehensive_Report_${projectName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    generator.save(filename);

    toast.dismiss('all-opps-report');
    toast.success('Comprehensive report generated successfully!');
  } catch (error: any) {
    console.error('Error generating all opportunities report:', error);
    toast.dismiss('all-opps-report');
    toast.error(error.message || 'Failed to generate comprehensive report');
  }
}

/**
 * Calculate total time reduction from all opportunities
 */
function calculateTotalTimeReduction(opportunities: OptimizationOpportunity[]): string {
  // Extract percentages and hours from time reduction strings
  let totalPercentage = 0;
  let totalHours = 0;
  let percentageCount = 0;
  let hoursCount = 0;

  opportunities.forEach(opp => {
    const timeReduction = opp.estimated_time_reduction;

    // Check for percentage (e.g., "30% reduction")
    const percentageMatch = timeReduction.match(/(\d+)%/);
    if (percentageMatch) {
      totalPercentage += parseInt(percentageMatch[1]);
      percentageCount++;
    }

    // Check for hours (e.g., "5-10 hours saved")
    const hoursMatch = timeReduction.match(/(\d+)(?:-(\d+))?\s*hours?/i);
    if (hoursMatch) {
      const minHours = parseInt(hoursMatch[1]);
      const maxHours = hoursMatch[2] ? parseInt(hoursMatch[2]) : minHours;
      totalHours += (minHours + maxHours) / 2; // Average
      hoursCount++;
    }
  });

  // Format output
  if (percentageCount > 0 && hoursCount > 0) {
    const avgPercentage = Math.round(totalPercentage / percentageCount);
    return `${avgPercentage}% reduction (${Math.round(totalHours)} hours saved)`;
  } else if (percentageCount > 0) {
    const avgPercentage = Math.round(totalPercentage / percentageCount);
    return `${avgPercentage}% reduction`;
  } else if (hoursCount > 0) {
    return `${Math.round(totalHours)} hours saved`;
  } else {
    return 'Significant time savings expected';
  }
}

/**
 * Generate Executive Summary only
 */
export async function generateExecutiveSummary(options: ReportGenerationOptions): Promise<void> {
  return generateAllReports(options);
}

// Export individual report generators and data types for direct use
export { generateExecutiveSummaryPDF } from './executiveSummaryPDF';
export { generateProcessFlowComparisonPDF } from './processFlowComparisonPDF';
export { generateImplementationRoadmapPDF } from './implementationRoadmapPDF';
export { generateImplementationGuidePDF } from './implementationGuidePDF';
export { generateConsolidatedReportPDF } from './consolidatedReportPDF';

export type { ExecutiveSummaryData } from './executiveSummaryPDF';
export type { ProcessFlowComparisonData, ProcessFlowStep } from './processFlowComparisonPDF';
export type { ImplementationRoadmapData } from './implementationRoadmapPDF';
export type { ImplementationGuideData } from './implementationGuidePDF';
export type { ConsolidatedReportData } from './consolidatedReportPDF';
